import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-oshveda-bgAlt flex items-center justify-center px-4 py-12" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8" data-testid="login-brand-link">
          <img src="/oshveda-logo.png" alt="Oshveda Naturals" className="h-14 w-14 object-contain" />
          <span className="font-serif text-2xl text-oshveda-primary">Oshveda Naturals</span>
        </Link>
        <div className="bg-white rounded-3xl border border-oshveda-borderSubtle p-8 md:p-10">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-oshveda-accent" />
            <span className="label-eyebrow">Admin Access</span>
          </div>
          <h1 className="font-serif text-3xl text-oshveda-ink mb-1">Welcome back.</h1>
          <p className="text-oshveda-ink2 mb-7">Sign in to manage your storefront.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <label className="block">
              <span className="label-eyebrow block mb-2">Email</span>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow block mb-2">Password</span>
              <input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
              />
            </label>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</div>}
            <button
              type="submit" disabled={submitting} data-testid="admin-login-submit"
              className="w-full px-8 py-3.5 rounded-full bg-oshveda-primary text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-primaryHover disabled:opacity-60 transition-all"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <Link to="/" className="block text-center text-sm text-oshveda-muted hover:text-oshveda-accent mt-6 transition-colors" data-testid="back-home">
          ← Back to store
        </Link>
      </div>
    </div>
  );
}
